import {PAYER} from '@/config'

const sessionName = {
    CREDENTIAL: "credential",
    CURRENT_USER: "currentUser",
    ADMIN_MODE: "adminMode"
}

// Глобальная переменная для ключа шифрования
let sessionCryptoKey = null;

// Инициализация сессии
export const initSession = async () => {
    if (sessionCryptoKey) return;

    try {
        const salt = PAYER.SESSION_KEY;
        const encoder = new TextEncoder();

        const keyMaterial = await window.crypto.subtle.importKey(
            "raw",
            encoder.encode(salt),
            "PBKDF2",
            false,
            ["deriveKey"]
        );

        sessionCryptoKey = await window.crypto.subtle.deriveKey(
            {
                name: "PBKDF2",
                salt: encoder.encode(salt + window.location.origin),
                iterations: 100000,
                hash: "SHA-256"
            },
            keyMaterial,
            { name: "AES-GCM", length: 256 },
            false,
            ["encrypt", "decrypt"]
        );
    } catch (error) {
        console.error("Session initialization failed:", error);
        throw new Error("SESSION_INIT_FAILED");
    }
};

// Шифрование только ваших данных
const encryptData = async (data) => {
    if (!sessionCryptoKey) await initSession();

    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encoder = new TextEncoder();

    // Шифруем только переданные данные без обёртки
    const encodedData = encoder.encode(JSON.stringify(data));

    const encrypted = await window.crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        sessionCryptoKey,
        encodedData
    );

    return {
        iv: Array.from(iv),
        data: Array.from(new Uint8Array(encrypted))
    };
};

// Дешифровка возвращает только ваши данные
const decryptData = async (encryptedData) => {
    if (!sessionCryptoKey) await initSession();

    try {
        const decrypted = await window.crypto.subtle.decrypt(
            { name: "AES-GCM", iv: new Uint8Array(encryptedData.iv) },
            sessionCryptoKey,
            new Uint8Array(encryptedData.data)
        );

        const decoded = new TextDecoder().decode(decrypted);
        return JSON.parse(decoded); // Возвращаем напрямую ваши данные
    } catch (error) {
        console.error("Decryption failed:", error);
        return null;
    }
};

const setSessionWithCrypto = async (key, data) => {
    try {
        const encrypted = await encryptData(data);
        sessionStorage.setItem(key, JSON.stringify(encrypted));
        return true;
    } catch (error) {
        console.error(`Error saving session data for key ${key}:`, error);
        return false;
    }
}

const getSessionWithCrypto = async (key) => {
    try {
        const item = sessionStorage.getItem(key);
        const encrypted = JSON.parse(item);

        return encrypted ? await decryptData(encrypted) : null;
    } catch (error) {
        console.error(`Error reading session data for key ${key}:`, error);
        return null;
    }
}

const setSession = (key, data) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
        console.error(`Error saving session data for key ${key}:`, error);
    }
}

const getSession = (key) => {
    try {
        const item = sessionStorage.getItem(key);
        return JSON.parse(item);
    } catch (error) {
        console.error(`Error reading session data for key ${key}:`, error);
        return null;
    }
}

const delSession = (key) => {
    sessionStorage.removeItem(key);
}

// Очистка сессии
export const clearSession = () => {
    Object.values(sessionName).forEach(key => {
        sessionStorage.removeItem(key);
    });

    sessionCryptoKey = null;
    console.debug("Session cleared");
};

// Функция для полного выхода с очисткой сессии
const logout = () => {
    // Очищаем sessionStorage
    Object.values(sessionName).forEach(key => {
        sessionStorage.removeItem(key);
    });

    // Сбрасываем ключ шифрования
    sessionCryptoKey = null;

    console.debug("User logged out, session cleared");
};

export {
    setSession,
    getSession,
    delSession,
    sessionName,
    setSessionWithCrypto,
    getSessionWithCrypto,
    logout
}